import { useState, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { X, Download, Smartphone, CheckCircle } from 'lucide-react';
import { cn } from '@/lib/utils';
import { usePWAInstall } from '@/hooks/usePWAInstall';

export const PWAInstallPrompt = () => {
  const { canInstall, canShowIOSInstall, isInstalled, isMobile, isIOS, isSafari, installPWA } = usePWAInstall();
  const [showInstallPrompt, setShowInstallPrompt] = useState(false);
  const [isInstalling, setIsInstalling] = useState(false);

  useEffect(() => {
    // Verificar se deve mostrar o prompt baseado no localStorage
    const dismissed = localStorage.getItem('pwa-prompt-dismissed');
    const remindLater = localStorage.getItem('pwa-prompt-remind');
    
    if (dismissed) {
      const dismissedTime = parseInt(dismissed);
      const oneDay = 24 * 60 * 60 * 1000;
      
      // Se foi dispensado há mais de 7 dias, mostrar novamente
      if (Date.now() - dismissedTime > 7 * oneDay) {
        localStorage.removeItem('pwa-prompt-dismissed');
      } else {
        return;
      }
    }
    
    if (remindLater) {
      const remindTime = parseInt(remindLater);
      
      if (Date.now() < remindTime) {
        return;
      } else {
        localStorage.removeItem('pwa-prompt-remind');
      }
    }

    // Mostrar o prompt: mobile imediato, desktop com atraso; iOS sempre imediato
    if (canShowIOSInstall) {
      setShowInstallPrompt(true);
      return;
    }

    if (canInstall) {
      if (isMobile) {
        setShowInstallPrompt(true);
      } else {
        const timer = setTimeout(() => setShowInstallPrompt(true), 1500);
        return () => clearTimeout(timer);
      }
    }
  }, [canInstall, canShowIOSInstall, isMobile]);

  const handleInstallClick = async () => {
    setIsInstalling(true);
    
    try {
      const success = await installPWA();
      if (success) {
        setShowInstallPrompt(false);
      }
    } catch (error) {
      console.error('Erro ao instalar PWA:', error);
    } finally {
      setIsInstalling(false);
    }
  };

  const handleDismiss = () => {
    setShowInstallPrompt(false);
    // Em desktop, permitir "não mostrar" por um tempo; em mobile, apenas fechar sessão atual
    if (!isMobile) {
      localStorage.setItem('pwa-prompt-dismissed', Date.now().toString());
    }
  };

  const handleRemindLater = () => {
    setShowInstallPrompt(false);
    // Em mobile, lembrar em 6h; em desktop, 24h
    const hours = isMobile ? 6 : 24;
    localStorage.setItem('pwa-prompt-remind', (Date.now() + hours * 60 * 60 * 1000).toString());
  };



  // Não mostrar se já está instalado ou não é mobile
  if (isInstalled || !isMobile || !showInstallPrompt) {
    return null;
  }

  return (
    <div className="fixed inset-0 z-50 flex items-end justify-center p-4 sm:items-center sm:p-6">
      {/* Overlay */}
      <div 
        className="fixed inset-0 bg-black/50 backdrop-blur-sm"
        onClick={handleDismiss}
      />
      
      {/* Card do Prompt */}
      <Card className="relative w-full max-w-sm animate-in slide-in-from-bottom-4 duration-300 sm:slide-in-from-bottom-0">
        <Button
          variant="ghost"
          size="icon"
          className="absolute right-2 top-2 h-6 w-6"
          onClick={handleDismiss}
        >
          <X className="h-4 w-4" />
        </Button>
        
        <CardHeader className="pb-3">
          <div className="flex items-center gap-3">
            <div className="flex h-10 w-10 items-center justify-center rounded-full bg-primary/10">
              <Smartphone className="h-5 w-5 text-primary" />
            </div>
            <div>
              <CardTitle className="text-lg">Instalar App</CardTitle>
              <CardDescription>
                Adicione à tela inicial para acesso rápido
              </CardDescription>
            </div>
          </div>
        </CardHeader>
        
        <CardContent className="space-y-4">
          <div className="space-y-2 text-sm">
            <div className="flex items-center gap-2">
              <CheckCircle className="h-4 w-4 text-green-500" />
              <span>Acesso offline</span>
            </div>
            <div className="flex items-center gap-2">
              <CheckCircle className="h-4 w-4 text-green-500" />
              <span>Notificações push</span>
            </div>
            <div className="flex items-center gap-2">
              <CheckCircle className="h-4 w-4 text-green-500" />
              <span>Experiência nativa</span>
            </div>
          </div>
          
          {/* UI de instalação: Android/desktop usa botão nativo; iOS mostra instruções */}
          {isIOS ? (
            <div className="space-y-3 text-sm">
              <div className="rounded-md bg-muted p-3">
                <p className="mb-2 font-medium">Como instalar no iOS</p>
                <ol className="list-decimal ml-5 space-y-1">
                  <li>Toque no ícone de compartilhar no Safari.</li>
                  <li>Selecione "Adicionar à Tela de Início".</li>
                  <li>Confirme com "Adicionar".</li>
                </ol>
                {!isSafari && (
                  <p className="mt-2 text-xs text-muted-foreground">Dica: em navegadores iOS, use o botão compartilhar do sistema.</p>
                )}
              </div>
            </div>
          ) : (
            <div className="flex gap-2">
              <Button
                onClick={handleInstallClick}
                disabled={isInstalling}
                className="flex-1"
              >
                {isInstalling ? (
                  <>
                    <div className="mr-2 h-4 w-4 animate-spin rounded-full border-2 border-white border-t-transparent" />
                    Instalando...
                  </>
                ) : (
                  <>
                    <Download className="mr-2 h-4 w-4" />
                    Instalar
                  </>
                )}
              </Button>
            </div>
          )}
          
          <div className="flex justify-between text-xs text-muted-foreground">
            <button
              onClick={handleRemindLater}
              className="hover:text-foreground transition-colors"
            >
              Lembrar depois
            </button>
            {!isMobile && (
              <button
                onClick={handleDismiss}
                className="hover:text-foreground transition-colors"
              >
                Não mostrar novamente
              </button>
            )}
          </div>
        </CardContent>
      </Card>
    </div>
  );
};